import {
  AppSessionAtomicOperation,
  AppSessionCellId,
  CREATE_EXPLORE_SPEC_SESSION_STATE,
  ExploreCellId,
  ExploreFieldOverrides,
  ExploreSpec,
  ExploreSpecSessionStateId,
  ExploreSpecSessionStateUpdatableFields,
  ExploreSpecUpdateActions,
  UPDATE_EXPLORE_SPEC_SESSION_STATE,
  exploreSpecActions,
  isPivotHqlSpecsChanged,
  mergeExploreSpecWithSessionState,
  uuid,
} from "@hex/common";
import { isEqual } from "lodash";

import { ExploreSpecSessionStateMP } from "../../redux/slices/appSessionMPSlice.js";

/**
 * Converts ExploreSpec actions directly to ExploreSpec session state operations
 */
export function getUpdateExploreSpecSessionStateAOs({
  actions,
  appSessionCellId,
  currentSpec,
  defaultFieldOverrides,
  exploreCellId,
  getExploreSpecSessionState,
}: {
  actions: ExploreSpecUpdateActions | ExploreSpecUpdateActions[];
  appSessionCellId: AppSessionCellId;
  exploreCellId: ExploreCellId;
  currentSpec: ExploreSpec;
  defaultFieldOverrides: ExploreFieldOverrides | null;
  getExploreSpecSessionState: (
    appSessionCellId: AppSessionCellId,
  ) => ExploreSpecSessionStateMP | undefined;
}): AppSessionAtomicOperation[] {
  const actionsArray = Array.isArray(actions) ? actions : [actions];
  const currentSessionState = getExploreSpecSessionState(appSessionCellId);

  let accumulatedSessionState: ExploreSpecSessionStateUpdatableFields = {
    fieldOverrides: currentSessionState?.fieldOverrides || null,
  };

  // We apply each action sequentially to build up to one final session state
  // This avoids the need to call multiple AOs to update the session state
  for (const action of actionsArray) {
    accumulatedSessionState = applyActionToSessionState(
      action,
      accumulatedSessionState,
      currentSpec,
      defaultFieldOverrides,
    );
  }
  return createSessionStateUpdateOps({
    currentSessionState,
    newSessionState: accumulatedSessionState,
    appSessionCellId,
    exploreCellId,
    currentSpec,
  });
}

/**
 * Applies a single ExploreSpec action to session state, returning an updated session state
 */
function applyActionToSessionState(
  action: ExploreSpecUpdateActions,
  sessionState: ExploreSpecSessionStateUpdatableFields,
  currentSpec: ExploreSpec,
  defaultFieldOverrides: ExploreFieldOverrides | null,
): ExploreSpecSessionStateUpdatableFields {
  switch (action.type) {
    case "exploreSpec/setFieldProperty":
      return applySetFieldPropertyToSessionState(
        action,
        sessionState,
        currentSpec,
        defaultFieldOverrides,
      );
    default:
      // Unsupported action types return unchanged session state, but do not throw
      console.error(
        `ExploreSpecSessionStateUpdate encountered an unsupported action type: ${action.type}`,
      );
      return sessionState;
  }
}

/**
 * Applies the setFieldProperty exploreSpecAction to an ExploreSpecSessionState
 */
function applySetFieldPropertyToSessionState(
  action: ReturnType<typeof exploreSpecActions.setFieldProperty>,
  sessionState: ExploreSpecSessionStateUpdatableFields,
  currentSpec: ExploreSpec,
  defaultFieldOverrides: ExploreFieldOverrides | null,
): ExploreSpecSessionStateUpdatableFields {
  const { fieldId, updateData } = action.payload;

  // Only handle properties that are supported in ExploreFieldOverride
  // Currently only supports sort -- in the future if additional props are added, we should make this more robust
  if (!updateData.sort) {
    return sessionState;
  }

  const updatedFieldOverrides = { ...sessionState.fieldOverrides };
  const defaultFieldOverride = defaultFieldOverrides?.[fieldId];
  const publishedSort = currentSpec.fields.find(
    (field) => field.id === fieldId,
  )?.sort;
  if (isEqual(updateData.sort, defaultFieldOverride?.sort ?? publishedSort)) {
    if (defaultFieldOverride == null) {
      delete updatedFieldOverrides[fieldId];
    } else {
      updatedFieldOverrides[fieldId] = defaultFieldOverride;
    }
  } else {
    updatedFieldOverrides[fieldId] = { sort: updateData.sort };
  }

  return {
    ...sessionState,
    fieldOverrides:
      Object.keys(updatedFieldOverrides).length > 0
        ? updatedFieldOverrides
        : null,
  };
}

/**
 * Generates atomic operations for changes to ExploreSpecSessionStates
 */
function createSessionStateUpdateOps({
  appSessionCellId,
  currentSessionState,
  currentSpec,
  exploreCellId,
  newSessionState,
}: {
  appSessionCellId: AppSessionCellId;
  currentSpec: ExploreSpec;
  exploreCellId: ExploreCellId;
  currentSessionState: ExploreSpecSessionStateMP | undefined;
  newSessionState: ExploreSpecSessionStateUpdatableFields;
}): AppSessionAtomicOperation[] {
  const ops: AppSessionAtomicOperation[] = [];

  const mergedSpec = mergeExploreSpecWithSessionState(
    currentSpec,
    newSessionState,
  );
  const shouldRunCell = isPivotHqlSpecsChanged(currentSpec, mergedSpec);

  // If the session state does not exist, we need to create it
  if (!currentSessionState) {
    if (newSessionState.fieldOverrides == null) return ops;
    ops.push(
      CREATE_EXPLORE_SPEC_SESSION_STATE.create({
        appSessionCellId,
        exploreCellId,
        exploreSpecSessionStateId: uuid() as ExploreSpecSessionStateId,
        options: newSessionState,
        shouldRunCell,
      }),
    );
  } else {
    if (
      !isEqual(
        currentSessionState.fieldOverrides,
        newSessionState.fieldOverrides,
      )
    ) {
      ops.push(
        UPDATE_EXPLORE_SPEC_SESSION_STATE.create({
          exploreSpecSessionStateId: currentSessionState.id,
          // We currently only support field overrides, but in the future if additional props are added, we should make this more robust
          key: "fieldOverrides",
          value: newSessionState.fieldOverrides,
          shouldRunCell,
        }),
      );
    }
  }
  return ops;
}
